export interface ISelectedCell {
  row: number
  col: number
}

/**
 * How the latest change to the selected cells was made: a block (plain click, Shift+click or drag)
 * replaces the selection with a rectangle, a pick (Cmd/Ctrl+click) toggles one cell, and an extend
 * (Cmd/Ctrl+drag) adds a rectangle to the selection the drag started from.
 */
export type CellSelectionGesture =
  { kind: 'block' } | { kind: 'pick' } | { kind: 'extend'; dragId: number }

export const BLOCK_GESTURE: CellSelectionGesture = { kind: 'block' }
export const PICK_GESTURE: CellSelectionGesture = { kind: 'pick' }

export interface ISelectionOrder {
  cells: ISelectedCell[]
  /** The order a Cmd/Ctrl+drag started from, kept for every step of that drag. */
  dragBase?: { dragId: number; cells: ISelectedCell[] }
}

export const EMPTY_SELECTION_ORDER: ISelectionOrder = { cells: [] }

const cellKey = ({ row, col }: ISelectedCell) => `${row}:${col}`

const toSortedCells = (activeCells: number[][]): ISelectedCell[] =>
  activeCells.flatMap((cols, row) =>
    cols?.length ? [...new Set(cols)].sort((a, b) => a - b).map((col) => ({ row, col })) : [],
  )

const getBaseOrder = (previous: ISelectionOrder, gesture: CellSelectionGesture) =>
  gesture.kind === 'extend' && previous.dragBase?.dragId === gesture.dragId
    ? previous.dragBase.cells
    : previous.cells

/**
 * Orders the selected cells for the tooltip's Difference: a block reads from its top-left cell,
 * any other selection keeps the order the user picked cells in, with cells added together
 * appended top-left first.
 */
export const orderSelectedCells = (
  previous: ISelectionOrder,
  activeCells: number[][],
  gesture: CellSelectionGesture,
): ISelectionOrder => {
  const cells = toSortedCells(activeCells)
  if (gesture.kind === 'block') {
    return { cells }
  }

  const base = getBaseOrder(previous, gesture)
  const selectedKeys = new Set(cells.map(cellKey))
  const kept = base.filter((cell) => selectedKeys.has(cellKey(cell)))
  const keptKeys = new Set(kept.map(cellKey))
  const added = cells.filter((cell) => !keptKeys.has(cellKey(cell)))
  return {
    cells: [...kept, ...added],
    dragBase: gesture.kind === 'extend' ? { dragId: gesture.dragId, cells: base } : undefined,
  }
}

/** The first value minus every value after it. */
export const getSelectionDifference = (values: number[]): number =>
  values.slice(1).reduce((difference, value) => difference - value, values[0] ?? 0)
